import { createSlice } from '@reduxjs/toolkit';

import ASYNC_STATE from '../../constants/AsyncState';
import { byId, byKey } from '../../helper/byKey';
import mergeObjects from '../../helper/mergeObjects';

import { allChannelsFetched } from '../channels/channelsThunks/allChannelsFetched';
import { createMemberEmail } from './membersThunks/createMemberEmail';
import { fetchMemberEmail } from './membersThunks/fetchMemberEmail';

const asyncState = {
  memberEmailFetch: {
    status: ASYNC_STATE.IDLE,
    error: null,
  },
  memberEmailCreate: {
    status: ASYNC_STATE.IDLE,
    error: null,
  },
};

const initialState = {
  ...asyncState,
  members: {},
  memberEmails: {},
  channelMembers: {},
};

const persistWhitelist = ['members', 'memberEmails', 'channelMembers'];

// hardcoded to avoid depending on redux-persist directly (mobile/web resolve different major versions of it)
const REHYDRATE = 'persist/REHYDRATE';

const membersSlice = createSlice({
  name: 'members',
  initialState,
  reducers: {
    memberUpdated: (state, action) => {
      const {
        member,
        member: { id },
      } = action.payload;
      state.members[id] = mergeObjects(state.members[id], member);
    },
    channelMemberRemoved: (state, action) => {
      const {
        channelId,
        member: { id: memberId },
      } = action.payload;
      const channelMembers = state.channelMembers[channelId];

      if (channelMembers) {
        state.channelMembers[channelId] = channelMembers.filter((id) => id !== memberId);
      }
    },
    channelMemberFetched: (state, action) => {
      const {
        channelId,
        member,
        member: { id: memberId },
      } = action.payload;
      state.members[memberId] = member;
      const channelMembers = state.channelMembers[channelId];
      if (!channelMembers) {
        state.channelMembers[channelId] = [memberId];
      } else {
        state.channelMembers[channelId] = [...new Set([...state.channelMembers[channelId], memberId])];
      }
    },
    resetMembersTyping: (state) => {
      for (const id in state.members) {
        state.members[id].isTyping = false;
      }
    },
  },
  extraReducers: {
    // isTyping is transient/live-only state: never trust a persisted value for it,
    // since the setTimeout that would normally clear it is lost if the app is killed
    // while someone is typing, leaving a stale `true` in storage indefinitely.
    //
    // Note: mutating `state.members` here makes redux-persist's autoRehydrate treat this
    // whole slice as "reducer modified" and skip its own inbound merge entirely (same
    // reason persistenceSlice.js re-applies `version` manually) - so every persisted
    // field of this slice must be restored explicitly below, not just `members`.
    [REHYDRATE]: (state, action) => {
      const incoming = action.payload?.members;
      if (!incoming) return;

      const sanitizedMembers = {};
      for (const id in incoming.members) {
        sanitizedMembers[id] = { ...incoming.members[id], isTyping: false };
      }
      state.members = mergeObjects(state.members, sanitizedMembers);
      state.memberEmails = mergeObjects(state.memberEmails, incoming.memberEmails ?? {});
      state.channelMembers = mergeObjects(state.channelMembers, incoming.channelMembers ?? {});
    },
    [fetchMemberEmail.pending]: (state) => {
      state.memberEmailFetch.status = ASYNC_STATE.LOADING;
    },
    [fetchMemberEmail.fulfilled]: (state, action) => {
      const { memberId, email } = action.payload;
      state.memberEmailFetch.status = ASYNC_STATE.SUCCEEDED;
      if (email) {
        state.memberEmails[memberId] = email;
      }
    },
    [fetchMemberEmail.rejected]: (state, action) => {
      state.memberEmailFetch.status = ASYNC_STATE.FAILED;
      state.error = action.error;
    },
    [createMemberEmail.pending]: (state) => {
      state.memberEmailCreate.status = ASYNC_STATE.LOADING;
    },
    [createMemberEmail.fulfilled]: (state, action) => {
      const { memberId, email } = action.payload;
      state.memberEmailCreate.status = ASYNC_STATE.SUCCEEDED;
      if (email) {
        state.memberEmails[memberId] = email;
      }
    },
    [createMemberEmail.rejected]: (state, action) => {
      state.memberEmailCreate.status = ASYNC_STATE.FAILED;
      state.error = action.error;
    },
    [allChannelsFetched.fulfilled]: (state, action) => {
      const { members } = action.payload;

      state.members = mergeObjects(state.members, byId(members));
      const membersByChannelId = byKey('channelId', members, true);
      for (const [channelId, membersForChannel] of Object.entries(membersByChannelId)) {
        state.channelMembers[channelId] = membersForChannel.map((member) => member.id);
      }
    },
  },
});

export { persistWhitelist as membersPersistWhitelist };
export const { memberUpdated, channelMemberRemoved, channelMemberFetched, resetMembersTyping } = membersSlice.actions;
export default membersSlice.reducer;
